import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
    Loader2,
    ArrowLeft,
    Trophy,
    TrendingUp,
    Timer,
    Repeat,
    Flame,
    Clock,
    BookOpen,
    MessageCircle,
    Headphones,
    Zap,
    Award,
    Users,
    Share2,
    Mic,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import SuccessTab from "@/components/SuccessTab";
import LeaderboardTab from "@/components/LeaderboardTab";
import ShareTab from "@/components/ShareTab";

const SKILL_LABELS = {
    comprehension: "Compréhension",
    expression: "Expression",
    prononciation: "Prononciation",
    grammaire: "Grammaire",
    fluidite: "Fluidité",
};

const KIND_META = {
    lesson: { icon: BookOpen, color: "text-emerald", label: "Leçon" },
    conversation: { icon: MessageCircle, color: "text-terracotta", label: "Conversation" },
    podcast: { icon: Headphones, color: "text-ink", label: "Podcast" },
    intensive: { icon: Zap, color: "text-emerald", label: "Intensif" },
    daily_voice: { icon: Mic, color: "text-terracotta", label: "Vocale" },
};

const TABS = [
    { key: "progression", label: "Progression", icon: TrendingUp },
    { key: "succes", label: "Succès", icon: Award },
    { key: "classement", label: "Classement", icon: Users },
    { key: "partage", label: "Partage", icon: Share2 },
];

function ActivityChart({ data }) {
    const max = useMemo(() => {
        let m = 1;
        for (const d of data) {
            const total = d.lessons + d.reviews + d.podcasts + d.conversations + d.intensive;
            if (total > m) m = total;
        }
        return m;
    }, [data]);

    return (
        <div className="bg-white border border-lexiBorder rounded-2xl p-4" data-testid="progress-activity-chart">
            <p className="text-[11px] tracking-[0.22em] uppercase text-inkSoft mb-3">
                Activité — 30 derniers jours
            </p>
            <div className="flex items-stretch gap-0.5 h-32 border-b border-lexiBorder">
                {data.map((d, i) => {
                    const total = d.lessons + d.reviews + d.podcasts + d.conversations + d.intensive;
                    const h = Math.round((total / max) * 100);
                    const isToday = i === data.length - 1;
                    return (
                        <div
                            key={d.date}
                            className="flex-1 h-full flex flex-col justify-end"
                            title={`${d.date} — ${total} activité(s)`}
                            data-testid={`activity-day-${i}`}
                        >
                            <div
                                className={`w-full rounded-t transition-colors ${isToday
                                    ? "bg-terracotta"
                                    : total === 0
                                        ? "bg-lexiBorder hover:bg-inkSoft/20"
                                        : "bg-emerald/80"}`}
                                style={{ height: `${Math.max(h, 10)}%`, minHeight: 6 }}
                            />
                        </div>
                    );
                })}
            </div>
            <div className="mt-2 flex items-center justify-between text-[10px] text-inkSoft">
                <span>il y a 30 jours</span>
                <span>aujourd&apos;hui</span>
            </div>
        </div>
    );
}

export default function ProgressPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = TABS.find((t) => t.key === searchParams.get("tab"))?.key || "progression";

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/dashboard/progress");
                setData(data);
            } catch (err) {
                toast.error(formatApiError(err));
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
        // Intentional mount-only fetch — the progress snapshot is fetched
        // once per page mount. Refresh happens by remounting via nav.
    }, []);

    function setActiveTab(key) {
        const next = new URLSearchParams(searchParams);
        if (key === "progression") next.delete("tab");
        else next.set("tab", key);
        setSearchParams(next, { replace: true });
    }

    if (loading) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center">
                <Loader2 className="w-6 h-6 text-terracotta animate-spin" />
            </div>
        );
    }
    if (!data) return null;

    const stats = data.stats || {};
    const skills = data.skills || {};
    const nextRev = data.next_review_at ? new Date(data.next_review_at) : null;
    const nextRevFmt = nextRev ? nextRev.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—";

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12" data-testid="progress-page">
            <button
                onClick={() => navigate("/dashboard")}
                className="text-inkSoft hover:text-ink text-sm inline-flex items-center gap-1 mb-4"
                data-testid="progress-back"
            >
                <ArrowLeft className="w-4 h-4" /> Tableau de bord
            </button>

            <p className="text-xs tracking-[0.28em] uppercase text-inkSoft mb-3">Progression</p>
            <h1 className="font-serif text-3xl md:text-4xl tracking-tight">
                Votre régularité vaut plus que votre volume.
            </h1>

            {/* Tabs */}
            <nav className="mt-8 border-b border-lexiBorder" data-testid="progress-tabs">
                <ul className="flex flex-wrap gap-1">
                    {TABS.map((t) => {
                        const active = activeTab === t.key;
                        const Icon = t.icon;
                        return (
                            <li key={t.key}>
                                <button
                                    onClick={() => setActiveTab(t.key)}
                                    data-testid={`progress-tab-${t.key}`}
                                    className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 transition-colors ${active
                                        ? "border-terracotta text-ink font-semibold"
                                        : "border-transparent text-inkSoft hover:text-ink"}`}
                                >
                                    <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
                                    {t.label}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            </nav>

            <div className="mt-8">
                {activeTab === "progression" && (
                    <ProgressionPanel
                        stats={stats}
                        skills={skills}
                        nextRevFmt={nextRevFmt}
                        activity={data.activity_30d || []}
                        recent={data.recent_sessions || []}
                    />
                )}
                {activeTab === "succes" && <SuccessTab />}
                {activeTab === "classement" && <LeaderboardTab />}
                {activeTab === "partage" && <ShareTab />}
            </div>
        </div>
    );
}

function ProgressionPanel({ stats, skills, nextRevFmt, activity, recent }) {
    return (
        <div>
            <p className="text-sm text-inkSoft max-w-2xl">
                Nous mesurons ce qui compte à long terme : les mots que vous retenez, la constance de vos jours actifs,
                et la finesse de chaque compétence — sans jamais inventer un score.
            </p>

            {/* Top-line counters */}
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="progress-counters">
                <div className="bg-white border border-lexiBorder rounded-2xl p-4">
                    <div className="flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase text-inkSoft">
                        <Flame className="w-3 h-3" /> Série
                    </div>
                    <p className="mt-1 font-serif text-3xl" data-testid="counter-streak">
                        {stats.streak_days || 0} <span className="text-sm text-inkSoft">jours</span>
                    </p>
                </div>
                <div className="bg-white border border-lexiBorder rounded-2xl p-4">
                    <div className="flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase text-inkSoft">
                        <Trophy className="w-3 h-3" /> Maîtrisés
                    </div>
                    <p className="mt-1 font-serif text-3xl text-emerald" data-testid="counter-mastered">
                        {stats.words_mastered || 0}
                    </p>
                </div>
                <div className="bg-white border border-lexiBorder rounded-2xl p-4">
                    <div className="flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase text-inkSoft">
                        <Repeat className="w-3 h-3" /> Actifs
                    </div>
                    <p className="mt-1 font-serif text-3xl" data-testid="counter-active">
                        {stats.words_active || 0}
                    </p>
                </div>
                <div className="bg-white border border-lexiBorder rounded-2xl p-4">
                    <div className="flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase text-inkSoft">
                        <Timer className="w-3 h-3" /> Fragiles
                    </div>
                    <p className="mt-1 font-serif text-3xl text-terracotta" data-testid="counter-fragile">
                        {stats.words_fragile || 0}
                    </p>
                </div>
            </div>

            <div className="mt-3 flex items-center gap-2 text-xs text-inkSoft">
                <Clock className="w-3.5 h-3.5" />
                Prochaine révision : <span className="text-ink font-medium" data-testid="progress-next-review">{nextRevFmt}</span>
            </div>

            {/* Skills */}
            <div className="mt-8">
                <p className="text-[11px] tracking-[0.22em] uppercase text-inkSoft mb-3">Compétences</p>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="progress-skills">
                    {Object.entries(SKILL_LABELS).map(([key, label]) => {
                        const s = skills[key] || { score: null, status: "pas encore évalué", samples: 0 };
                        const evaluated = s.status === "évalué";
                        return (
                            <li
                                key={key}
                                data-testid={`skill-${key}`}
                                className="bg-white border border-lexiBorder rounded-2xl p-4"
                            >
                                <div className="flex items-baseline justify-between gap-2">
                                    <span className="font-serif text-base">{label}</span>
                                    <span
                                        className={`text-[10px] tracking-[0.22em] uppercase font-semibold ${evaluated ? "text-emerald" : "text-inkSoft"}`}
                                    >
                                        {s.samples} donnée{s.samples > 1 ? "s" : ""}
                                    </span>
                                </div>
                                {evaluated ? (
                                    <div className="mt-2 flex items-baseline gap-2">
                                        <span className="font-serif text-3xl text-ink" data-testid={`skill-${key}-score`}>
                                            {s.score}
                                        </span>
                                        <span className="text-sm text-inkSoft">/ 100</span>
                                    </div>
                                ) : (
                                    <p className="mt-2 text-sm text-inkSoft italic" data-testid={`skill-${key}-empty`}>
                                        pas encore évalué
                                    </p>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </div>

            {/* Activity chart */}
            <div className="mt-8">
                <ActivityChart data={activity} />
            </div>

            {/* Recent sessions */}
            <div className="mt-8">
                <p className="text-[11px] tracking-[0.22em] uppercase text-inkSoft mb-3">Historique récent</p>
                {(recent || []).length === 0 ? (
                    <p className="text-sm text-inkSoft italic" data-testid="progress-history-empty">
                        Rien à afficher — vos futures sessions apparaîtront ici.
                    </p>
                ) : (
                    <ul className="space-y-2" data-testid="progress-recent">
                        {recent.map((r, i) => {
                            const meta = KIND_META[r.kind] || { icon: BookOpen, color: "text-ink", label: r.kind };
                            const Icon = meta.icon;
                            return (
                                <li key={i} className="bg-white border border-lexiBorder rounded-xl p-3 flex items-center gap-3 text-sm">
                                    <Icon className={`w-4 h-4 ${meta.color}`} />
                                    <span className="text-[10px] tracking-[0.22em] uppercase text-inkSoft w-20">{meta.label}</span>
                                    <span className="flex-1 font-serif">{r.title_fr}</span>
                                    <span className="text-[11px] text-inkSoft">{r.at || "—"}</span>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
}
